import {Component, DestroyRef, Input, OnDestroy, OnInit, inject} from '@angular/core';
import {
  BalansEnResultatenrekening,
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte
} from '@snelstart-ventures/fiscaal/domain-logic';
import {AbstractControl, FormArray, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, filter, Observable, shareReplay, tap} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-slot-activa',
  templateUrl: './spec-slot-activa.component.html',
  styleUrls: ['./spec-slot-activa.component.scss'],
  imports: [
    SharedUiDetailsModule,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class SpecSlotActivaComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  public index = 0;

  public balansEnResultatenRekeningFg = new FormGroup({
    balansEnResultatenrekening: new FormArray<FormGroup>([]),
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  get balansEnResultatenrekening(): FormArray<FormGroup> {
    return this.balansEnResultatenRekeningFg.get('balansEnResultatenrekening') as FormArray;
  }

  private readonly activaFields: string[] = [
    'goodwill', 'overigeImmaterieleActiva', 'gebouwenEnTerreinen', 'machinesEnInstallaties',
    'andereVasteActiva', 'vorderingenLangGroepsmaatschappijen', 'vorderingenLangParticipanten',
    'overigeFinancieelVasteActiva', 'deelnemingen', 'voorraden', 'onderhandenWerk',
    'vorderingDebiteuren', 'vorderingenOmzetbelasting', 'vorderingenKortGroepsmaatschappijen',
    'vorderingenKortParticipanten', 'vorderingenOverig', 'effecten', 'liquideMiddelen'
  ];

  private isFiscaleEenheid(): boolean {
    return this.dataService.isFiscaleEenheid.value;
  }

  private getConsolidatedRow(rows: BalansEnResultatenrekening[]): BalansEnResultatenrekening | undefined {
    return rows.find(row => row.naamVennootschap === 'Consolidatie');
  }

  private calculateConsolidatedSlotsByField(rows: BalansEnResultatenrekening[], fieldName: string): number {
    return rows.reduce((sum, row) => {
      if (row.naamVennootschap === 'Consolidatie') {
        return sum;
      }

      const multiplier = row.naamVennootschap === 'Eliminatie' ? -1 : 1;
      const value =
        ((row as unknown as Record<string, Array<{ slotbalans?: number | null }> | undefined>)[fieldName]
          ?.at(0)
          ?.slotbalans ?? 0);
      return sum + value * multiplier;
    }, 0);
  }

  private buildConsolidatedFromRows(
    rows: BalansEnResultatenrekening[],
    base: BalansEnResultatenrekening
  ): BalansEnResultatenrekening {
    const consolidated = {
      ...base,
      naamVennootschap: 'Consolidatie',
    } as BalansEnResultatenrekening;

    const consolidatedRecord = consolidated as unknown as Record<
      string,
      Array<{ openingsbalans?: number | null; slotbalans?: number | null; totaalActivaSlot?: number | null }> | undefined
    >;

    let totaalActivaSlot = 0;
    for (const fieldName of this.activaFields) {
      const slotbalans = this.calculateConsolidatedSlotsByField(rows, fieldName);
      const existingField = consolidatedRecord[fieldName]?.at(0) ?? {};
      consolidatedRecord[fieldName] = [{
        ...existingField,
        slotbalans: slotbalans === 0 ? undefined : slotbalans,
      }];
      totaalActivaSlot += slotbalans;
    }

    const existingTotaal = consolidatedRecord['totaal']?.at(0) ?? {};
    consolidatedRecord['totaal'] = [{
      ...existingTotaal,
      totaalActivaSlot: totaalActivaSlot === 0 ? undefined : totaalActivaSlot,
    }];

    return consolidated;
  }

  private getInitialRows(vpbAangifte: VpbAangifte): BalansEnResultatenrekening[] {
    if (this.isFiscaleEenheid()) {
      const enkelvoudigeRows = [...(vpbAangifte.enkelvoudigeResultatenFiscaleEenheid || [])]
        .filter(row => row.naamVennootschap !== 'Consolidatie');
      const consolidatedRow = {
        ...vpbAangifte.balansEnResultatenrekening,
        naamVennootschap: 'Consolidatie',
      } as BalansEnResultatenrekening;

      return [...enkelvoudigeRows, consolidatedRow];
    }

    return [
      {
        ...vpbAangifte.balansEnResultatenrekening,
        naamVennootschap:
          vpbAangifte.balansEnResultatenrekening?.naamVennootschap ??
          vpbAangifte.identificatie?.naamLichaam,
      } as BalansEnResultatenrekening,
    ];
  }

  public calculateTotaalActivaSlot(entityControl: AbstractControl): number {
    const entityFormGroup = entityControl as FormGroup;
    return this.activaFields.reduce((total, fieldName) => {
      const fieldArray = entityFormGroup.get(fieldName) as FormArray | null;
      if (!fieldArray) return total;
      for (let j = 0; j < fieldArray.length; j++) {
        total += fieldArray.at(j).get('slotbalans')?.value ?? 0;
      }
      return total;
    }, 0);
  }

  private syncRowDisabledState(entityGroup: FormGroup): void {
    const isConsolidatie = entityGroup.get('naamVennootschap')?.value === 'Consolidatie';

    for (const fieldName of this.activaFields) {
      const fieldArray = entityGroup.get(fieldName) as FormArray | null;
      const slotbalansControl = fieldArray?.at(0)?.get('slotbalans');
      if (!slotbalansControl) {
        continue;
      }

      if (isConsolidatie) {
        slotbalansControl.disable({emitEvent: false});
      } else {
        slotbalansControl.enable({emitEvent: false});
      }
    }
  }

  ngOnInit(): void {
    this.balansEnResultatenRekeningFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.recalculateTotalen();
      this.saveForm();
    });
  }

  private recalculateTotalen(): void {
    const formArray = this.balansEnResultatenrekening;
    const isFe = this.isFiscaleEenheid();

    let consolidatieIndex = -1;
    for (let i = 0; i < formArray.length; i++) {
      const entityGroup = formArray.at(i) as FormGroup;
      if (entityGroup.get('naamVennootschap')?.value === 'Consolidatie') {
        consolidatieIndex = i;
        break;
      }
    }

    for (let i = 0; i < formArray.length; i++) {
      if (isFe && i === consolidatieIndex) {
        continue;
      }

      const entityGroup = formArray.at(i) as FormGroup;
      const totaal = this.calculateTotaalActivaSlot(entityGroup);
      const totaalArray = entityGroup.get('totaal') as FormArray | null;
      if (totaalArray && totaalArray.length > 0) {
        totaalArray.at(0).get('totaalActivaSlot')?.setValue(totaal, {emitEvent: false});
      }
    }
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = this.getInitialRows(vpbAangifte);
    const formArray = this.balansEnResultatenrekening;

    this.index = lijst.length;
    if (this.index < 2) {
      this.index = 2;
    }

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i);
        control.patchValue(lijst[i], {emitEvent: false});
        this.syncRowDisabledState(control);
      } else {
        const formGroup = this.dataService.createBalansEnResultatenRekeningFormGroup(lijst[i]);
        formArray.push(formGroup as FormGroup, {emitEvent: false});
        this.syncRowDisabledState(formGroup as FormGroup);
      }
    }
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
  }

  saveForm(): void {
    this.balansEnResultatenRekeningFg.updateValueAndValidity({emitEvent: false});
    const rows =
      (this.balansEnResultatenRekeningFg.getRawValue().balansEnResultatenrekening ?? []) as BalansEnResultatenrekening[];
    const isFe = this.isFiscaleEenheid();
    const enkelvoudigeRows = rows.filter(row => row.naamVennootschap !== 'Consolidatie');

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const consolidatedRow = this.getConsolidatedRow(rows);
      const fallbackLastRow = rows.length > 0 ? rows[rows.length - 1] : undefined;
      const balansEnResultatenrekening = isFe
        ? this.buildConsolidatedFromRows(
          rows,
          consolidatedRow ?? fallbackLastRow ?? currentEntity.aangifte.balansEnResultatenrekening
        )
        : (rows[0] ?? currentEntity.aangifte.balansEnResultatenrekening);

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          balansEnResultatenrekening,
          enkelvoudigeResultatenFiscaleEenheid: isFe ? enkelvoudigeRows : undefined,
        }
      } as CorporateTaxEntity;
    });
  }
}
